import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, Trash2, Save, Eye, EyeOff, Search, X,
  LayoutGrid, Move, Users, GripVertical, StickyNote, Printer, Pencil, Check,
  Music, Wine, Utensils, Mic2, DoorOpen, Square,
} from "lucide-react";
import { useAdminAuth, LoginScreen } from "./adminAuth";
import AdminHeader from "../components/AdminHeader";
import RoundTable from "../components/RoundTable";
import RoomCanvas, { getFurnitureStyle } from "../components/RoomCanvas";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TABLE_GAP_X = 280;
const TABLE_GAP_Y = 300;
const ROOM_W = 1400;

const nextGridPosition = (count) => {
  const cols = Math.max(1, Math.floor((ROOM_W - 80) / TABLE_GAP_X));
  const col = count % cols;
  const row = Math.floor(count / cols);
  return { x: 60 + col * TABLE_GAP_X, y: 60 + row * TABLE_GAP_Y };
};

const newTable = (idx) => {
  const { x, y } = nextGridPosition(idx);
  return {
    id: `t_${Date.now()}_${idx}`,
    name: `Tisch ${idx + 1}`,
    seats: 8,
    assignments: Array(8).fill(""),
    x, y,
  };
};

const FURNITURE_TYPES = [
  { type: "dance", label: "Tanzfläche", Icon: Music },
  { type: "bar", label: "Bar", Icon: Wine },
  { type: "buffet", label: "Buffet", Icon: Utensils },
  { type: "stage", label: "Bühne", Icon: Mic2 },
  { type: "door", label: "Eingang", Icon: DoorOpen },
  { type: "custom", label: "Bereich", Icon: Square },
];

const newFurniture = (type, idx) => {
  const s = getFurnitureStyle(type);
  return {
    id: `f_${Date.now()}_${idx}`,
    type,
    name: "",
    x: 100 + (idx % 4) * 60,
    y: 100 + (idx % 4) * 60,
    w: s.defaultW,
    h: s.defaultH,
  };
};

export default function AdminSeating() {
  const { password, authed, checking, login, logout } = useAdminAuth();
  const [tables, setTables] = useState([]);
  const [furniture, setFurniture] = useState([]);
  const [notes, setNotes] = useState("");
  const [manualGuests, setManualGuests] = useState([]);
  const [published, setPublished] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rsvpNames, setRsvpNames] = useState([]);
  const [nameToRsvpId, setNameToRsvpId] = useState({}); // maps display name → rsvp id (only main names, not "+N")
  const [editing, setEditing] = useState(null); // seat assignment
  const [editingFurniture, setEditingFurniture] = useState(null);
  const [view, setView] = useState("list"); // 'list' | 'room'
  const [guestFilter, setGuestFilter] = useState("");
  const [newGuest, setNewGuest] = useState("");
  const [showNotes, setShowNotes] = useState(false);
  // Print list styling — persisted per browser via localStorage.
  const readLS = (key, fallback, min, max) => {
    if (typeof window === "undefined") return fallback;
    const v = parseFloat(window.localStorage.getItem(key) || "");
    return Number.isFinite(v) && v >= min && v <= max ? v : fallback;
  };
  const [printFontPt, setPrintFontPt] = useState(() => readLS("seating_print_font_pt", 16, 8, 40));
  const [printTitlePt, setPrintTitlePt] = useState(() => readLS("seating_print_title_pt", 13, 8, 28));
  const [printLineHeight, setPrintLineHeight] = useState(() => readLS("seating_print_line_height", 1.35, 1, 2.5));
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem("seating_print_font_pt", String(printFontPt));
    window.localStorage.setItem("seating_print_title_pt", String(printTitlePt));
    window.localStorage.setItem("seating_print_line_height", String(printLineHeight));
  }, [printFontPt, printTitlePt, printLineHeight]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [seating, rsvps] = await Promise.all([
        axios.get(`${API}/seating/admin`, { params: { password } }),
        axios.get(`${API}/rsvps`, { params: { password } }),
      ]);
      const loaded = (seating.data?.tables || []).map((t, i) => {
        if (typeof t.x === "number" && typeof t.y === "number" && (t.x || t.y)) return t;
        const pos = nextGridPosition(i);
        return { ...t, x: t.x || pos.x, y: t.y || pos.y };
      });
      setTables(loaded);
      setFurniture(seating.data?.furniture || []);
      setNotes(seating.data?.notes || "");
      setManualGuests(seating.data?.manual_guests || []);
      setPublished(!!seating.data?.published);
      const list = [];
      const idMap = {};
      (rsvps.data || [])
        .filter((r) => (r.attending || "").toLowerCase() === "ja")
        .forEach((r) => {
          list.push(r.name);
          idMap[r.name] = r.id;
          for (let i = 0; i < (r.guests || 0); i++) {
            list.push(`${r.name} (+${i + 1})`);
          }
        });
      setRsvpNames(list);
      setNameToRsvpId(idMap);
    } catch (err) {
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  }, [password]);

  useEffect(() => {
    if (authed) load();
  }, [authed, load]);

  // Build full guest pool (RSVPs + manual) and assignment status
  const usedNames = useMemo(
    () => new Set(tables.flatMap((t) => (t.assignments || []).filter(Boolean))),
    [tables]
  );
  const allGuests = useMemo(() => {
    const seen = new Set();
    const out = [];
    [...rsvpNames, ...manualGuests].forEach((n) => {
      const key = n.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        out.push(n);
      }
    });
    return out;
  }, [rsvpNames, manualGuests]);

  if (checking) return <div className="min-h-screen bg-sage-50" />;
  if (!authed) return <LoginScreen onSubmit={login} />;

  const updateTable = (idx, patch) => {
    setTables((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  };

  const setSeats = (idx, seats) => {
    const s = Math.max(0, Math.min(20, parseInt(seats, 10) || 0));
    setTables((prev) => {
      const next = [...prev];
      const cur = next[idx];
      const a = [...(cur.assignments || [])];
      while (a.length < s) a.push("");
      a.length = s;
      next[idx] = { ...cur, seats: s, assignments: a };
      return next;
    });
  };

  const addTable = () => setTables((p) => [...p, newTable(p.length)]);
  const removeTable = (idx) => {
    if (!window.confirm(`Tisch "${tables[idx]?.name}" wirklich löschen?`)) return;
    setTables((p) => p.filter((_, i) => i !== idx));
  };

  const assign = (tIdx, sIdx, name) => {
    setTables((prev) => {
      const next = [...prev];
      const a = [...(next[tIdx].assignments || [])];
      a[sIdx] = name;
      next[tIdx] = { ...next[tIdx], assignments: a };
      return next;
    });
    setEditing(null);
  };

  const moveTable = (id, x, y) => {
    setTables((prev) => prev.map((t) => (t.id === id ? { ...t, x, y } : t)));
  };

  const moveFurniture = (id, x, y) => {
    setFurniture((prev) => prev.map((f) => (f.id === id ? { ...f, x, y } : f)));
  };

  const addFurniture = (type) =>
    setFurniture((p) => [...p, newFurniture(type, p.length)]);

  const updateFurniture = (id, patch) => {
    setFurniture((prev) => prev.map((f) => (f.id === id ? { ...f, ...patch } : f)));
  };

  const removeFurniture = (id) => {
    setFurniture((prev) => prev.filter((f) => f.id !== id));
    setEditingFurniture(null);
  };

  const seatClickFromCanvas = (tableId, seatIdx) => {
    const idx = tables.findIndex((t) => t.id === tableId);
    if (idx >= 0) setEditing({ tableIdx: idx, seatIdx, prefill: "" });
  };

  // Triggered when a guest name is dropped onto a table: open dialog to pick exact seat
  const tableDropName = (tableId, name) => {
    const idx = tables.findIndex((t) => t.id === tableId);
    if (idx < 0) return;
    const table = tables[idx];
    // find first free seat (default seat suggestion); fallback to 0
    const firstFree = (table.assignments || []).findIndex((a) => !a);
    setEditing({
      tableIdx: idx,
      seatIdx: firstFree >= 0 ? firstFree : 0,
      prefill: name,
    });
  };

  const addManualGuest = () => {
    const v = newGuest.trim();
    if (!v) return;
    if ([...rsvpNames, ...manualGuests].some((n) => n.toLowerCase() === v.toLowerCase())) {
      toast.error("Dieser Name ist schon in der Liste");
      return;
    }
    setManualGuests((p) => [...p, v]);
    setNewGuest("");
  };

  const removeManualGuest = (name) => {
    setManualGuests((p) => p.filter((n) => n !== name));
  };

  // Rename a guest. Works for both manual guests and RSVP-derived main names.
  // For RSVP main names, calls backend PATCH which also updates seating assignments server-side.
  const renameGuest = async (oldName, newNameRaw) => {
    const newName = (newNameRaw || "").trim();
    if (!newName || newName === oldName) return;
    // Avoid duplicates
    const exists = [...rsvpNames, ...manualGuests].some(
      (n) => n.toLowerCase() === newName.toLowerCase() && n !== oldName
    );
    if (exists) {
      toast.error("Dieser Name ist schon vergeben");
      return;
    }
    // Update local seat assignments (both main + "(+N)" companions)
    const renameInAssignments = (a) =>
      (a || []).map((v) => {
        if (v === oldName) return newName;
        if (v.startsWith(`${oldName} (+`)) return v.replace(oldName, newName);
        return v;
      });
    setTables((prev) =>
      prev.map((t) => ({ ...t, assignments: renameInAssignments(t.assignments) }))
    );

    if (manualGuests.includes(oldName)) {
      setManualGuests((p) => p.map((n) => (n === oldName ? newName : n)));
      toast.success(`„${oldName}" → „${newName}"`);
      return;
    }

    const rsvpId = nameToRsvpId[oldName];
    if (rsvpId) {
      try {
        await axios.patch(
          `${API}/rsvps/${rsvpId}`,
          { name: newName },
          { params: { password } }
        );
        // Refresh local rsvp name list + id map
        setRsvpNames((prev) => prev.map((n) =>
          n === oldName
            ? newName
            : n.startsWith(`${oldName} (+`)
            ? n.replace(oldName, newName)
            : n
        ));
        setNameToRsvpId((prev) => {
          const next = { ...prev };
          delete next[oldName];
          next[newName] = rsvpId;
          return next;
        });
        toast.success(`„${oldName}" → „${newName}" (RSVP aktualisiert)`);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Umbenennen fehlgeschlagen");
      }
    } else {
      toast.error("Begleitungen können nicht direkt umbenannt werden — Hauptgast umbenennen");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/seating`,
        { published, tables, furniture, notes, manual_guests: manualGuests },
        { params: { password } }
      );
      toast.success(
        published
          ? "Gespeichert · Live auf der Hochzeitsseite"
          : "Gespeichert · Nur für Admins sichtbar"
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const totalSeats = tables.reduce((s, t) => s + (t.seats || 0), 0);
  const filledSeats = tables.reduce(
    (s, t) => s + (t.assignments || []).filter(Boolean).length,
    0
  );

  return (
    <div data-testid="admin-seating" className="min-h-screen bg-sage-50 pb-32">
      <div className="no-print">
        <AdminHeader onLogout={logout} />
      </div>

      <main className="container mx-auto px-4 md:px-12 py-10 no-print">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
          <div>
            <h2 className="font-serif text-2xl md:text-3xl text-sage-800">Tischplan</h2>
            <p className="text-sm text-sage-600 mt-1 max-w-2xl">
              Erstelle Tische und Möbel, weise Gäste per Drag & Drop oder Klick zu.
              Der Plan ist standardmäßig <strong>nur für Admins</strong> sichtbar.
            </p>
            <p className="mt-2 text-xs text-sage-500">
              Belegung: <strong>{filledSeats} / {totalSeats}</strong> Plätze ·{" "}
              {tables.length} Tisch{tables.length === 1 ? "" : "e"} · {furniture.length} Möbel
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0 flex-wrap">
            <PrintStepper
              label="Namen"
              value={printFontPt}
              onChange={setPrintFontPt}
              min={8}
              max={40}
              step={1}
              unit="pt"
              testidPrefix="print-font"
            />
            <PrintStepper
              label="Tisch"
              value={printTitlePt}
              onChange={setPrintTitlePt}
              min={8}
              max={28}
              step={1}
              unit="pt"
              testidPrefix="print-title"
            />
            <PrintStepper
              label="Zeilen"
              value={printLineHeight}
              onChange={setPrintLineHeight}
              min={1}
              max={2.5}
              step={0.05}
              unit=""
              format={(v) => v.toFixed(2)}
              testidPrefix="print-line-height"
            />
            <button
              type="button"
              onClick={() => window.print()}
              data-testid="seating-print"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs uppercase tracking-wider border bg-white text-sage-700 border-sage-300 hover:bg-sage-50"
            >
              <Printer size={14} />
              Drucken
            </button>
            <button
              type="button"
              onClick={() => setShowNotes((s) => !s)}
              data-testid="seating-toggle-notes"
              className={`inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs uppercase tracking-wider border ${
                showNotes
                  ? "bg-sage-500 text-white border-sage-500"
                  : "bg-white text-sage-700 border-sage-300 hover:bg-sage-50"
              }`}
            >
              <StickyNote size={14} />
              Notizen
            </button>
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                data-testid="seating-published-toggle"
                type="checkbox"
                checked={published}
                onChange={(e) => setPublished(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-12 h-7 bg-sage-200 peer-checked:bg-sage-500 rounded-full relative transition-colors">
                <div
                  className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                    published ? "translate-x-5" : ""
                  }`}
                />
              </div>
              <span className="text-sm text-sage-700 inline-flex items-center gap-1.5">
                {published ? <Eye size={14} /> : <EyeOff size={14} />}
                {published ? "Live" : "Nur Admin"}
              </span>
            </label>
          </div>
        </div>

        {/* Notes panel */}
        {showNotes && (
          <div
            data-testid="seating-notes-panel"
            className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-6"
          >
            <label className="block">
              <span className="text-xs uppercase tracking-[0.2em] text-amber-700 inline-flex items-center gap-1.5">
                <StickyNote size={12} /> Private Admin-Notizen
              </span>
              <textarea
                data-testid="seating-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                placeholder="z.B. Tante Erika nicht neben Onkel Klaus. 2 Vegetarier an Tisch 3."
                className="mt-2 w-full rounded-lg border border-amber-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
              />
            </label>
            <p className="text-[10px] text-amber-700 mt-1">
              Diese Notizen sind nur für eingeloggte Admins sichtbar.
            </p>
          </div>
        )}

        {/* View switcher */}
        <div className="mb-4 inline-flex items-center bg-white border border-sage-200 rounded-full p-1">
          <button
            type="button"
            data-testid="seating-view-list"
            onClick={() => setView("list")}
            className={`inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider rounded-full transition-colors ${
              view === "list" ? "bg-sage-500 text-white shadow" : "text-sage-700 hover:bg-sage-50"
            }`}
          >
            <LayoutGrid size={14} /> Liste
          </button>
          <button
            type="button"
            data-testid="seating-view-room"
            onClick={() => setView("room")}
            className={`inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider rounded-full transition-colors ${
              view === "room" ? "bg-sage-500 text-white shadow" : "text-sage-700 hover:bg-sage-50"
            }`}
          >
            <Move size={14} /> Raumplan
          </button>
        </div>

        {loading ? (
          <div className="text-center text-sage-400 py-16">Lädt...</div>
        ) : view === "list" ? (
          <>
            {tables.length === 0 ? (
              <div className="bg-white border border-dashed border-sage-300 rounded-2xl p-12 text-center">
                <p className="text-sage-500 mb-4">Noch keine Tische angelegt.</p>
                <button
                  onClick={addTable}
                  className="inline-flex items-center gap-2 rounded-full bg-sage-500 hover:bg-sage-600 text-white px-6 py-3 text-sm uppercase tracking-wide"
                >
                  <Plus size={16} /> Ersten Tisch anlegen
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {tables.map((t, i) => (
                  <TableCard
                    key={t.id}
                    table={t}
                    onChangeName={(v) => updateTable(i, { name: v })}
                    onChangeSeats={(v) => setSeats(i, v)}
                    onRemove={() => removeTable(i)}
                    onSeatClick={(sIdx) =>
                      setEditing({ tableIdx: i, seatIdx: sIdx, prefill: "" })
                    }
                  />
                ))}
              </div>
            )}
            {tables.length > 0 && (
              <div className="mt-8 text-center">
                <button
                  data-testid="seating-add-table"
                  onClick={addTable}
                  className="inline-flex items-center gap-2 rounded-full border border-sage-400 text-sage-700 hover:bg-sage-100 px-6 py-3 text-sm uppercase tracking-wide bg-white"
                >
                  <Plus size={16} /> Weiteren Tisch hinzufügen
                </button>
              </div>
            )}
          </>
        ) : (
          // Room view: guest sidebar + canvas + furniture toolbar
          <div className="grid grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)] gap-4">
            <GuestSidebar
              guests={allGuests}
              usedNames={usedNames}
              manualGuests={manualGuests}
              nameToRsvpId={nameToRsvpId}
              filter={guestFilter}
              setFilter={setGuestFilter}
              newGuest={newGuest}
              setNewGuest={setNewGuest}
              onAddManual={addManualGuest}
              onRemoveManual={removeManualGuest}
              onRename={renameGuest}
            />

            <div className="space-y-3">
              <FurnitureToolbar onAdd={addFurniture} onAddTable={addTable} />
              <p className="text-xs text-sage-500">
                Ziehe Tische und Möbel im Raum. Ziehe einen Gast aus der Seitenliste auf
                einen Tisch, um ihn zuzuweisen. Klicke auf ein Möbelstück zum Bearbeiten.
              </p>
              <RoomCanvas
                tables={tables}
                furniture={furniture}
                onMoveTable={moveTable}
                onMoveFurniture={moveFurniture}
                onSeatClick={seatClickFromCanvas}
                onTableDropName={tableDropName}
                onFurnitureClick={(id) => setEditingFurniture(id)}
              />
            </div>
          </div>
        )}
      </main>

      {/* Sticky save bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-sage-200 shadow-lg z-20 no-print">
        <div className="container mx-auto px-6 md:px-12 py-3 md:py-4 flex items-center justify-between gap-4">
          <span className="text-xs md:text-sm text-sage-600 hidden sm:inline">
            {'Änderungen erst nach „Speichern" wirksam.'}
          </span>
          <button
            data-testid="seating-save"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-full bg-sage-500 hover:bg-sage-600 disabled:bg-sage-300 text-white px-6 py-2.5 text-sm uppercase tracking-wide ml-auto"
          >
            <Save size={16} /> {saving ? "Speichert..." : "Speichern"}
          </button>
        </div>
      </div>

      {editing && (
        <SeatAssignDialog
          table={tables[editing.tableIdx]}
          seatIdx={editing.seatIdx}
          prefill={editing.prefill}
          rsvpNames={allGuests}
          usedNames={usedNames}
          onClose={() => setEditing(null)}
          onAssign={(name, explicitSeat) =>
            assign(
              editing.tableIdx,
              typeof explicitSeat === "number" ? explicitSeat : editing.seatIdx,
              name
            )
          }
        />
      )}

      {editingFurniture && (
        <FurnitureDialog
          furniture={furniture.find((f) => f.id === editingFurniture)}
          onClose={() => setEditingFurniture(null)}
          onUpdate={(patch) => updateFurniture(editingFurniture, patch)}
          onRemove={() => removeFurniture(editingFurniture)}
        />
      )}

      {/* Print-only layout — visible only when window.print() is invoked */}
      <PrintLayout
        tables={tables}
        furniture={furniture}
        guestFontPt={printFontPt}
        titleFontPt={printTitlePt}
        lineHeight={printLineHeight}
      />
    </div>
  );
}

const PrintStepper = ({ label, value, onChange, min, max, step, unit, format, testidPrefix }) => {
  const clamp = (n) => Math.min(max, Math.max(min, Math.round(n / step) * step));
  const fmt = format || ((v) => (Number.isInteger(v) ? String(v) : v.toFixed(2)));
  return (
    <div
      className="inline-flex items-center gap-1 px-2 py-1.5 rounded-full text-xs uppercase tracking-wider border bg-white text-sage-700 border-sage-300"
      title={`${label} — Schrift/Zeilenabstand für die Print-Liste`}
    >
      <span className="hidden md:inline text-[10px]">{label}</span>
      <button
        type="button"
        data-testid={`${testidPrefix}-decrease`}
        onClick={() => onChange(clamp(value - step))}
        className="w-5 h-5 rounded-full hover:bg-sage-100 flex items-center justify-center text-sage-700"
        aria-label="Kleiner"
      >
        −
      </button>
      <span
        data-testid={`${testidPrefix}-value`}
        className="min-w-[2rem] text-center font-mono text-[11px] tabular-nums text-sage-800"
      >
        {fmt(value)}{unit ? ` ${unit}` : ""}
      </span>
      <button
        type="button"
        data-testid={`${testidPrefix}-increase`}
        onClick={() => onChange(clamp(value + step))}
        className="w-5 h-5 rounded-full hover:bg-sage-100 flex items-center justify-center text-sage-700"
        aria-label="Größer"
      >
        +
      </button>
    </div>
  );
};

const PrintLayout = ({ tables, furniture, guestFontPt = 16, titleFontPt = 13, lineHeight = 1.35 }) => {
  // Only tables that actually have someone assigned
  const filledTables = tables.filter(
    (t) => (t.assignments || []).filter(Boolean).length > 0
  );
  return (
    <div className="print-only print-seating">
      {/* -------------------- Page 1: Plan (A4 landscape) -------------------- */}
      <section className="print-page-plan">
        <div className="text-center mb-4">
          <p className="text-[10pt] uppercase tracking-[0.4em] text-sage-500">
            Nico &amp; Nicole · 01.08.2026 · Gut Haferkorn
          </p>
          <h1 className="font-serif text-[26pt] text-sage-800 mt-0.5 leading-tight">
            Sitzplan
          </h1>
        </div>
        <div
          className="mx-auto"
          style={{
            width: 1400 * 0.7,
            height: 900 * 0.7,
          }}
        >
          <RoomCanvas
            tables={tables}
            furniture={furniture}
            readOnly
            forcedScale={0.7}
          />
        </div>
      </section>

      {/* -------------------- Page 2: Guest list (A4 portrait) -------------------- */}
      <section className="print-page-list">
        <div className="text-center mb-10">
          <p className="text-[11pt] uppercase tracking-[0.5em] text-sage-500 font-serif">
            Sitzplan
          </p>
          <h1
            className="font-script text-sage-700 mt-4 leading-none"
            style={{ fontSize: "48pt" }}
          >
            Nico und Nicole
          </h1>
          <p className="text-[10pt] uppercase tracking-[0.3em] text-sage-400 mt-3">
            01.08.2026 · Gut Haferkorn
          </p>
        </div>

        <div className="grid grid-cols-3 gap-x-6 gap-y-8">
          {filledTables.map((t) => {
            const guests = (t.assignments || []).filter(Boolean);
            return (
              <div key={t.id} className="print-list-card text-center">
                <h3
                  className="font-serif uppercase tracking-[0.35em] text-sage-500 mb-3"
                  style={{ fontSize: `${titleFontPt}pt` }}
                >
                  {t.name}
                </h3>
                <ul className="space-y-1">
                  {guests.map((name, i) => (
                    <li
                      key={i}
                      className="font-serif text-sage-800"
                      style={{ fontSize: `${guestFontPt}pt`, lineHeight }}
                    >
                      {name}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
};

// -------- Guest sidebar (draggable) --------
const COMPANION_RE = /\s\(\+\d+\)$/;

const GuestSidebar = ({
  guests, usedNames, manualGuests, nameToRsvpId, filter, setFilter,
  newGuest, setNewGuest, onAddManual, onRemoveManual, onRename,
}) => {
  const filtered = guests.filter((n) =>
    n.toLowerCase().includes(filter.toLowerCase())
  );
  const unassigned = filtered.filter((n) => !usedNames.has(n));
  const assigned = filtered.filter((n) => usedNames.has(n));

  const isRenameable = (n) =>
    manualGuests.includes(n) || (!!nameToRsvpId[n] && !COMPANION_RE.test(n));

  return (
    <aside
      data-testid="guest-sidebar"
      className="bg-white border border-sage-200 rounded-2xl p-4 lg:sticky lg:top-4 lg:self-start max-h-[80vh] flex flex-col"
    >
      <h3 className="font-serif text-lg text-sage-800 flex items-center gap-2">
        <Users size={16} /> Gäste
      </h3>
      <p className="text-[11px] text-sage-500 mt-0.5 mb-3">
        Auf einen Tisch ziehen zum Zuweisen
      </p>

      <div className="relative mb-3">
        <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-sage-400" />
        <input
          data-testid="guest-filter"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Suchen..."
          className="w-full rounded-lg border border-sage-200 bg-sage-50 pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sage-400"
        />
      </div>

      <div className="flex gap-2 mb-3">
        <input
          data-testid="guest-add-input"
          value={newGuest}
          onChange={(e) => setNewGuest(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onAddManual();
            }
          }}
          placeholder="Gast manuell hinzufügen"
          className="flex-1 rounded-lg border border-sage-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sage-400"
        />
        <button
          type="button"
          data-testid="guest-add-btn"
          onClick={onAddManual}
          className="rounded-lg bg-sage-500 hover:bg-sage-600 text-white px-3 py-2"
          aria-label="Gast hinzufügen"
        >
          <Plus size={16} />
        </button>
      </div>

      <div className="overflow-y-auto -mx-1 px-1 flex-1">
        {unassigned.length === 0 && assigned.length === 0 && (
          <p className="text-xs text-sage-400 text-center py-8">
            Keine Gäste gefunden.
          </p>
        )}

        {unassigned.length > 0 && (
          <>
            <p className="text-[10px] uppercase tracking-widest text-sage-500 mt-1 mb-2">
              Offen ({unassigned.length})
            </p>
            <ul className="space-y-1.5 mb-4">
              {unassigned.map((n) => (
                <GuestChip
                  key={n}
                  name={n}
                  isManual={manualGuests.includes(n)}
                  canRename={isRenameable(n)}
                  onRemoveManual={() => onRemoveManual(n)}
                  onRename={onRename}
                />
              ))}
            </ul>
          </>
        )}

        {assigned.length > 0 && (
          <>
            <p className="text-[10px] uppercase tracking-widest text-sage-400 mt-1 mb-2">
              Platziert ({assigned.length})
            </p>
            <ul className="space-y-1.5">
              {assigned.map((n) => (
                <GuestChip
                  key={n}
                  name={n}
                  isManual={manualGuests.includes(n)}
                  canRename={isRenameable(n)}
                  onRemoveManual={() => onRemoveManual(n)}
                  onRename={onRename}
                  dimmed
                />
              ))}
            </ul>
          </>
        )}
      </div>
    </aside>
  );
};

const GuestChip = ({ name, isManual, canRename, onRemoveManual, onRename, dimmed }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(name);

  const commit = () => {
    setEditing(false);
    const v = value.trim();
    if (v && v !== name) {
      onRename?.(name, v);
    } else {
      setValue(name);
    }
  };

  if (editing) {
    return (
      <li
        data-testid={`guest-chip-${name}`}
        className="flex items-center gap-2 rounded-lg border border-sage-400 bg-white px-2 py-1.5 text-sm"
      >
        <Pencil size={12} className="text-sage-500 shrink-0" />
        <input
          autoFocus
          data-testid={`guest-rename-input-${name}`}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
            if (e.key === "Escape") {
              setValue(name);
              setEditing(false);
            }
          }}
          onBlur={commit}
          className="flex-1 min-w-0 bg-transparent outline-none text-sage-800"
        />
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={commit}
          className="text-sage-600 hover:text-sage-800"
          aria-label="Speichern"
        >
          <Check size={14} />
        </button>
      </li>
    );
  }

  return (
    <li
      data-testid={`guest-chip-${name}`}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData("text/plain", name);
        e.dataTransfer.effectAllowed = "move";
      }}
      className={`group flex items-center gap-2 rounded-lg border px-2 py-1.5 text-sm cursor-grab active:cursor-grabbing select-none ${
        dimmed
          ? "border-sage-100 bg-sage-50 text-sage-400 line-through"
          : "border-sage-200 bg-white text-sage-800 hover:border-sage-400 hover:bg-sage-50"
      }`}
    >
      <GripVertical size={12} className="text-sage-400 shrink-0" />
      <span className="truncate flex-1">{name}</span>
      {canRename && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setValue(name);
            setEditing(true);
          }}
          title="Namen bearbeiten"
          data-testid={`guest-rename-${name}`}
          className="opacity-0 group-hover:opacity-100 text-sage-400 hover:text-sage-700"
        >
          <Pencil size={12} />
        </button>
      )}
      {isManual && (
        <button
          type="button"
          onClick={onRemoveManual}
          title="Manuellen Gast entfernen"
          className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600"
        >
          <X size={12} />
        </button>
      )}
    </li>
  );
};

// -------- Furniture toolbar --------
const FurnitureToolbar = ({ onAdd, onAddTable }) => (
  <div className="bg-white border border-sage-200 rounded-2xl p-3 flex flex-wrap items-center gap-2">
    <span className="text-xs uppercase tracking-widest text-sage-500 mr-2">Hinzufügen:</span>
    <button
      type="button"
      data-testid="add-table-room"
      onClick={onAddTable}
      className="inline-flex items-center gap-1.5 rounded-full bg-sage-500 hover:bg-sage-600 text-white px-3 py-1.5 text-xs"
    >
      <Plus size={12} /> Tisch
    </button>
    {FURNITURE_TYPES.map(({ type, label, Icon }) => (
      <button
        key={type}
        type="button"
        data-testid={`add-furniture-${type}`}
        onClick={() => onAdd(type)}
        className="inline-flex items-center gap-1.5 rounded-full bg-white border border-sage-300 text-sage-700 hover:bg-sage-50 px-3 py-1.5 text-xs"
      >
        <Icon size={12} /> {label}
      </button>
    ))}
  </div>
);

// -------- Table card (list view) --------
const TableCard = ({ table, onChangeName, onChangeSeats, onRemove, onSeatClick }) => (
  <div className="bg-white border border-sage-200 rounded-2xl p-5 shadow-sm">
    <div className="flex items-center gap-2 mb-3">
      <input
        data-testid={`table-name-${table.id}`}
        value={table.name}
        onChange={(e) => onChangeName(e.target.value)}
        className="flex-1 rounded-lg border border-sage-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sage-400"
        placeholder="Tisch-Name"
      />
      <input
        data-testid={`table-seats-${table.id}`}
        type="number"
        min="0"
        max="20"
        value={table.seats}
        onChange={(e) => onChangeSeats(e.target.value)}
        className="w-20 rounded-lg border border-sage-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sage-400"
      />
      <button
        type="button"
        onClick={onRemove}
        className="rounded-lg bg-white border border-red-200 text-red-500 hover:bg-red-50 p-2"
        aria-label="Tisch löschen"
      >
        <Trash2 size={16} />
      </button>
    </div>
    <div className="flex justify-center my-2">
      <RoundTable table={table} size={240} onSeatClick={onSeatClick} highlightEmpty />
    </div>
    <p className="text-[11px] text-sage-500 text-center mt-2">
      Klicke auf einen Platz, um einen Gast zuzuweisen
    </p>
  </div>
);

// -------- Seat assign dialog --------
const SeatAssignDialog = ({ table, seatIdx, prefill, rsvpNames, usedNames, onClose, onAssign }) => {
  const [query, setQuery] = useState(prefill || "");
  const current = table.assignments?.[seatIdx] || "";

  const filtered = rsvpNames.filter((n) =>
    n.toLowerCase().includes(query.toLowerCase())
  );

  // If prefill was provided (drag drop), allow quick assign to a different seat
  const freeSeats = (table.assignments || []).reduce((acc, a, i) => {
    if (!a || i === seatIdx) acc.push(i);
    return acc;
  }, []);
  const [targetSeat, setTargetSeat] = useState(seatIdx);

  return (
    <div
      data-testid="seat-assign-dialog"
      className="fixed inset-0 z-50 bg-sage-950/70 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-white rounded-t-3xl md:rounded-2xl shadow-2xl border border-sage-200"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-sage-100">
          <div>
            <h3 className="font-serif text-xl text-sage-800">
              {table.name} · Platz {targetSeat + 1}
            </h3>
            {current && targetSeat === seatIdx && (
              <p className="text-xs text-sage-500 mt-0.5">
                Aktuell: <strong>{current}</strong>
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-sage-400 hover:text-sage-700 p-2">
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Seat picker — useful for drag-and-drop drops onto a table */}
          {freeSeats.length > 1 && (
            <div>
              <span className="block text-xs uppercase tracking-[0.2em] text-sage-600 mb-2">
                Sitzplatz wählen
              </span>
              <div className="flex flex-wrap gap-1.5">
                {(table.assignments || []).map((_a, i) => {
                  const occupied = !!table.assignments?.[i] && i !== seatIdx;
                  const isTarget = i === targetSeat;
                  return (
                    <button
                      key={i}
                      type="button"
                      disabled={occupied}
                      onClick={() => setTargetSeat(i)}
                      data-testid={`pick-seat-${i}`}
                      className={`w-9 h-9 rounded-full text-xs font-medium border-2 ${
                        isTarget
                          ? "bg-sage-500 text-white border-sage-600"
                          : occupied
                          ? "bg-sage-100 border-sage-200 text-sage-400 cursor-not-allowed"
                          : "bg-white border-sage-300 text-sage-700 hover:border-sage-500"
                      }`}
                    >
                      {i + 1}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <label className="block">
            <span className="block text-xs uppercase tracking-[0.2em] text-sage-600 mb-2">
              Name (frei eingeben oder Vorschlag wählen)
            </span>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-sage-400" />
              <input
                data-testid="seat-name-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="z.B. Anna Müller"
                className="w-full rounded-lg border border-sage-300 bg-white pl-9 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-sage-400"
                autoFocus
              />
            </div>
          </label>

          {rsvpNames.length > 0 && (
            <div className="max-h-48 overflow-y-auto rounded-lg border border-sage-100">
              {(filtered.length ? filtered : rsvpNames).slice(0, 30).map((n) => {
                const used = usedNames.has(n) && n !== current;
                return (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setQuery(n)}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-sage-50 flex items-center justify-between border-b border-sage-100 last:border-0"
                  >
                    <span className={used ? "text-sage-400 line-through" : "text-sage-800"}>
                      {n}
                    </span>
                    {used && (
                      <span className="text-[10px] uppercase tracking-wide text-sage-400">
                        bereits platziert
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-sage-100 flex items-center justify-between gap-3">
          {current && targetSeat === seatIdx && (
            <button
              type="button"
              onClick={() => onAssign("")}
              className="text-sm text-red-500 hover:text-red-700 underline"
            >
              Platz freigeben
            </button>
          )}
          <button
            type="button"
            data-testid="seat-assign-confirm"
            onClick={() => {
              // If a different target seat was picked, write to that one instead.
              if (targetSeat !== seatIdx) {
                // bubble up via two-step: parent's `onAssign` writes to the seatIdx prop.
                // We adapt: re-call with explicit seat using parent contract — re-render needed.
                // Simpler: expose by writing into the chosen seat through onAssign.
                // We accomplish this by also writing through a custom callback signal:
                onAssign(query.trim(), targetSeat);
              } else {
                onAssign(query.trim());
              }
            }}
            disabled={!query.trim()}
            className="ml-auto rounded-full bg-sage-500 hover:bg-sage-600 disabled:bg-sage-300 text-white px-5 py-2 text-sm uppercase tracking-wide"
          >
            Übernehmen
          </button>
        </div>
      </div>
    </div>
  );
};

// -------- Furniture edit dialog --------
const FurnitureDialog = ({ furniture: f, onClose, onUpdate, onRemove }) => {
  if (!f) return null;
  const s = getFurnitureStyle(f.type);
  const label = FURNITURE_TYPES.find((t) => t.type === f.type)?.label || s.label;
  return (
    <div
      data-testid="furniture-dialog"
      className="fixed inset-0 z-50 bg-sage-950/70 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-white rounded-t-3xl md:rounded-2xl shadow-2xl border border-sage-200"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-sage-100">
          <h3 className="font-serif text-xl text-sage-800">{label} bearbeiten</h3>
          <button onClick={onClose} className="text-sage-400 hover:text-sage-700 p-2">
            <X size={20} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <label className="block">
            <span className="block text-xs uppercase tracking-[0.2em] text-sage-600 mb-2">
              Eigener Name (optional)
            </span>
            <input
              data-testid="furniture-name-input"
              value={f.name}
              onChange={(e) => onUpdate({ name: e.target.value })}
              placeholder={s.label}
              className="w-full rounded-lg border border-sage-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sage-400"
            />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-xs uppercase tracking-[0.2em] text-sage-600 mb-2">
                Breite
              </span>
              <input
                type="number"
                min="40"
                max="800"
                value={Math.round(f.w)}
                onChange={(e) => onUpdate({ w: parseFloat(e.target.value) || 40 })}
                className="w-full rounded-lg border border-sage-300 bg-white px-3 py-2 text-sm"
              />
            </label>
            <label className="block">
              <span className="block text-xs uppercase tracking-[0.2em] text-sage-600 mb-2">
                Höhe
              </span>
              <input
                type="number"
                min="40"
                max="800"
                value={Math.round(f.h)}
                onChange={(e) => onUpdate({ h: parseFloat(e.target.value) || 40 })}
                className="w-full rounded-lg border border-sage-300 bg-white px-3 py-2 text-sm"
              />
            </label>
          </div>
        </div>
        <div className="px-5 py-4 border-t border-sage-100 flex items-center justify-between gap-3">
          <button
            type="button"
            data-testid="furniture-remove"
            onClick={onRemove}
            className="text-sm text-red-500 hover:text-red-700 underline inline-flex items-center gap-1.5"
          >
            <Trash2 size={14} /> Entfernen
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full bg-sage-500 hover:bg-sage-600 text-white px-5 py-2 text-sm uppercase tracking-wide"
          >
            Fertig
          </button>
        </div>
      </div>
    </div>
  );
};
